import { useState, useEffect, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import { 
  useGetChatsQuery, 
  useGetMessagesQuery, 
  useSendMessageMutation 
} from '../../../store';
import { 
  WhatsAppChat, 
  WhatsAppMessage, 
  ChatFilters, 
  SendMessageRequest 
} from '../types';
import { addNotification } from '../../../store';

/**
 * Hook principal pour la gestion des chats WhatsApp
 * Exploite RTK Query et Redux Toolkit pour un state management optimal
 */
export const useChat = (chatId?: string) => {
  const dispatch = useAppDispatch();
  
  // État Redux
  const selectedAgent = useAppSelector(state => state.agents.selectedAgent);
  const user = useAppSelector(state => state.auth.user);
  
  // Local state pour l'optimistic updates
  const [optimisticMessages, setOptimisticMessages] = useState<WhatsAppMessage[]>([]);
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(null);
  
  // RTK Query hooks
  const { 
    data: chats = [], 
    isLoading: chatsLoading, 
    error: chatsError,
    refetch: refetchChats 
  } = useGetChatsQuery({
    agentId: selectedAgent?.id,
    limit: 50
  }, {
    pollingInterval: 5000, // Polling pour temps réel
    refetchOnFocus: true,
    refetchOnReconnect: true
  });
  
  const { 
    data: messages = [], 
    isLoading: messagesLoading, 
    error: messagesError,
    refetch: refetchMessages 
  } = useGetMessagesQuery({
    chatId: chatId || '',
    limit: 100,
    offset: 0
  }, {
    skip: !chatId,
    pollingInterval: 2000, // Polling plus fréquent pour les messages
    refetchOnFocus: true
  });
  
  const [sendMessage, { 
    isLoading: sendingMessage, 
    error: sendError 
  }] = useSendMessageMutation();
  
  // Fonction pour envoyer un message avec optimistic update
  const handleSendMessage = useCallback(async (content: string, type: WhatsAppMessage['type'] = 'text') => {
    if (!chatId || !content.trim()) return;
    
    const optimisticId = `optimistic-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Optimistic update
    const optimisticMessage: WhatsAppMessage = {
      id: optimisticId,
      chatId,
      content: content.trim(),
      type,
      sender: 'agent',
      timestamp: new Date().toISOString(),
      status: 'sending',
      metadata: { optimisticId }
    };
    
    setOptimisticMessages(prev => [...prev, optimisticMessage]);
    
    try {
      const result = await sendMessage({
        chatId,
        content: content.trim(),
        type
      }).unwrap();
      
      // Supprimer le message optimistic et actualiser
      setOptimisticMessages(prev => 
        prev.filter(msg => msg.metadata?.optimisticId !== optimisticId)
      );
      
      // Notification de succès
      dispatch(addNotification({
        type: 'success',
        title: 'Message envoyé',
        message: 'Votre message a été envoyé avec succès',
        duration: 3000
      }));
      
      // Forcer un refetch pour synchroniser
      refetchMessages();
      refetchChats();
      
      return result;
    } catch (error) {
      // Marquer le message optimistic comme failed
      setOptimisticMessages(prev => 
        prev.map(msg => 
          msg.metadata?.optimisticId === optimisticId 
            ? { ...msg, status: 'failed' as const }
            : msg
        )
      );
      
      // Notification d'erreur
      dispatch(addNotification({
        type: 'error',
        title: 'Erreur d\'envoi',
        message: 'Impossible d\'envoyer le message. Veuillez réessayer.',
        duration: 5000
      }));
      
      throw error;
    }
  }, [chatId, sendMessage, dispatch, refetchMessages, refetchChats]);
  
  // Fonction pour retry un message failed
  const retryMessage = useCallback(async (failedMessage: WhatsAppMessage) => {
    if (failedMessage.status !== 'failed') return;
    
    // Supprimer le message failed de l'optimistic state
    setOptimisticMessages(prev => 
      prev.filter(msg => msg.id !== failedMessage.id)
    );
    
    // Renvoyer le message
    return handleSendMessage(failedMessage.content, failedMessage.type);
  }, [handleSendMessage]);
  
  // Gestion du typing indicator
  const handleTyping = useCallback((isTyping: boolean) => {
    if (!chatId) return;
    
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    
    if (isTyping) {
      // Simuler un typing indicator (sera remplacé par WebSocket)
      const timeout = setTimeout(() => {
        // Arrêter le typing après 3 secondes
      }, 3000);
      setTypingTimeout(timeout);
    }
  }, [chatId, typingTimeout]);
  
  // Nettoyage des timeouts
  useEffect(() => {
    return () => {
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
    };
  }, [typingTimeout]);
  
  // Messages combinés (reels + optimistic)
  const allMessages = [...messages, ...optimisticMessages].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );
  
  // Chat sélectionné
  const selectedChat = chats.find(chat => chat.id === chatId);
  
  // Statistiques du chat
  const chatStats = {
    totalMessages: allMessages.length,
    unreadCount: selectedChat?.unreadCount || 0,
    lastActivity: selectedChat?.updatedAt,
    aiResponseRate: allMessages.filter(msg => msg.sender === 'ai').length / Math.max(allMessages.length, 1) * 100
  };
  
  return {
    // Données
    chats,
    selectedChat,
    messages: allMessages,
    chatStats,
    
    // États de chargement
    isLoading: chatsLoading || messagesLoading,
    isSending: sendingMessage,
    
    // Erreurs
    error: chatsError || messagesError || sendError,
    
    // Actions
    sendMessage: handleSendMessage,
    retryMessage,
    refetchChats,
    refetchMessages,
    handleTyping,
    
    // Utilitaires
    hasUnreadMessages: (chatId: string) => {
      const chat = chats.find(c => c.id === chatId);
      return (chat?.unreadCount || 0) > 0;
    },
    
    getLastMessage: (chatId: string) => {
      const chatMessages = allMessages.filter(msg => msg.chatId === chatId);
      return chatMessages[chatMessages.length - 1];
    }
  };
};
